import { LoggingSection } from "./LoggingSection";

export { LoggingSection };

export type LoggingSectionType = LoggingSection | string;

/**
 * Výčet hlavních závažností pro logování.
 * - Musí to být čísla, protože úroveň logování se obecně zadává jako číslo. Tento výčet je pouze pro lepší čitelnost.
 * - Čím vyšší číslo, tím méně je zpráva závažná.
 */
export enum LoggingLevel {
    /**
     * Pro totální vypnutí logování. (Určeno pouze pro zadání prahu logování.)
     */
    off = 0,

    error = 1000,
    warning = 2000,
    info = 3000,
    debug = 4000,
}

/**
 * Závažnost logovaného záznamu. Čím větší číslo, tím menší závažnost.
 * Používá se:
 * - jako součást zalogování
 * - jako práh (section) loggeru říkající `"zaloguji pouze záznamy s tímto nebo menším číslem"`.
 */
export type LoggingLevelType = LoggingLevel | number;

/**
 * Číslo v intervalu `<0.1000)`. Slouží jako parametr do metody {@link ISectionLogger.fineLog}.
 * A Představuje pozici mezi jednotlivými hlavními závažnostmi {@link LoggingLevel}.
 */
export type FineLevelOffset = number;

/**
 * Typ nosné zprávy pro zalogování.
 * Typicky asi bude string. Ale kvůli obecnosti je `any`.
 */
export type LoggingMessage = any;

/**
 * Zdroj zprávy pro zalogování. Buď je to zprává přímo, nebo funkce, která tuto zprávu vrací.
 *
 * Funkce to může být kvůli performance v souvislosti s prahem logování. Pokud by sestrojení zprávy bylo časově náročné,
 * tak je rozumné toto sestrojení odložit až poté, co víme, že k zalogování dojde. (Tj. až po projití prahem závažnosti.)
 */
export type LoggingMessageOrGenerator = LoggingMessage | (() => LoggingMessage);

/**
 * Typ logovacích metod v {@link ISectionLogger}. Metody mohou příjmout:
 * - buď přímo zprávu
 * - nebo funkci generující zprávu (kvůli performane, abychom nesestavovali náročné zprávy, které se pak stejně nezalogují)
 */
export type SectionLoggerMethod = (messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]) => void;

/**
 * Typ návratové hodnoty funkce {@link substituteArguments}.
 */
export type SubstituteMessageType = {
    substitutedMessage: LoggingMessage;
    unsubstitutedArguments: any[];
};

/**
 * Konfigurační vlastnosti logování.
 */
export interface ILoggingSettings {
    /**
     * Každá oblast může mít - volitelně - svůj vlastní práh závažnosti, od kterého se bude logovat.
     */
    readonly sections: {
        readonly [key in LoggingSectionType]: LoggingLevel;
    };

    /**
     * Defaultní práh závažnosti, od kterého se bude logovat. Použito, pokud pro danou oblast není závažnost definována explicitně.
     */
    readonly defaultLevel: LoggingLevel;
}

/**
 * Informace potřebné pro pozdější provední příkazu `ISectionLogger.group` nebo `ISectionLogger.groupCollapsed`.
 */
export type PendingGroup = Readonly<{
    messageOrGenerator: LoggingMessageOrGenerator;
    args: any[];
    isCollapsed: boolean;
}>;
